import { useEffect, useState } from "react";
import { Menu, X, ArrowRight, MessageSquare } from "lucide-react";
import Logo from "./Logo";
import { scrollToCalculator } from "@/lib/notify";
import ContactModal from "./ContactModal";

const NAV_ITEMS = [
    { label: "Home", href: "#home" },
    { label: "Product", href: "#product" },
    { label: "Pricing", href: "#pricing" },
    { label: "Compliance", href: "#compliance" },
    { label: "Corridors", href: "#corridors" },
    { label: "Resources", href: "#resources" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleAnchor = (e, href) => {
        if (!href?.startsWith("#")) return;
        const id = href.slice(1);
        const target = document.getElementById(id);
        if (target) {
            e.preventDefault();
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            setOpen(false);
        } else {
            // Anchor target not on current page — navigate home with hash.
            e.preventDefault();
            window.location.href = "/" + href;
        }
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-zinc-950/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8 md:py-4">
                <a href="#home" onClick={(e) => handleAnchor(e, "#home")} data-testid="navbar-logo">
                    <Logo variant="dark" />
                </a>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.label}
                            href={item.href}
                            data-testid={`nav-link-${item.label.toLowerCase()}`}
                            onClick={(e) => handleAnchor(e, item.href)}
                            className="px-3 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="nav-contact-us-btn"
                        onClick={() => setContactOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-colors backdrop-blur-sm"
                    >
                        <MessageSquare className="h-4 w-4" />
                        Contact Us
                    </button>
                    <button
                        type="button"
                        data-testid="nav-get-started-btn"
                        onClick={scrollToCalculator}
                        className="group ml-1 inline-flex items-center gap-1.5 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-zinc-950 glow-gold hover:bg-gold-400 transition-all hover:-translate-y-0.5"
                    >
                        Get Started
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                </div>

                <button
                    type="button"
                    aria-label="Open menu"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="lg:hidden border-t border-white/10 bg-zinc-950/95 backdrop-blur-xl"
                >
                    <div className="mx-auto max-w-7xl px-4 py-6 flex flex-col gap-1">
                        {NAV_ITEMS.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                                onClick={(e) => handleAnchor(e, item.href)}
                                className="px-3 py-3 text-base font-medium text-zinc-200 rounded-lg hover:bg-white/5"
                            >
                                {item.label}
                            </a>
                        ))}
                        <button
                            type="button"
                            data-testid="nav-mobile-contact-us-btn"
                            onClick={() => {
                                setContactOpen(true);
                                setOpen(false);
                            }}
                            className="mt-3 inline-flex h-12 items-center justify-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-5 text-base font-semibold text-emerald-100 hover:bg-emerald-500/20"
                        >
                            <MessageSquare className="h-4 w-4" />
                            Contact Us
                        </button>
                        <button
                            type="button"
                            data-testid="nav-mobile-get-started-btn"
                            onClick={() => {
                                scrollToCalculator();
                                setOpen(false);
                            }}
                            className="mt-2 inline-flex h-12 items-center justify-center rounded-full bg-gold px-5 text-base font-bold text-zinc-950 glow-gold"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            )}
            <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
        </header>
    );
}
